// src/services/timesheetService.js - COMPLETE UPDATED VERSION
import { supabase } from './supabase';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import FingerprintParser from './fingerprintParser';
import * as XLSX from 'xlsx';

// Initialize dayjs plugins
dayjs.extend(customParseFormat);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

// Configuration from your payroll settings
const PAYROLL_CONFIG = {
  startDate: 28,
  endDate: 30,
  overtimeThreshold: 195,
  regularOvertimeRate: 1.5,
  holidayOvertimeRate: 2.0
};

// Helper function to get month name
const getMonthName = (monthIndex) => {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  return months[monthIndex];
};

// Convert XLSX to CSV format for existing parser
const convertXLSXToCSVFormat = (fileContent, fileName) => {
  try {
    console.log('Converting XLSX/CSV to standard format...');

    let jsonData;

    // Check if content is a string (CSV file) or ArrayBuffer (Excel file)
    if (typeof fileContent === 'string') {
      // Parse CSV content directly
      console.log('Parsing CSV content...');
      const lines = fileContent.split('\n');
      jsonData = lines.map(line => {
        // Simple CSV parsing (handles comma-separated values)
        return line.split(',').map(cell => cell.trim());
      });

      // Find the maximum number of columns to ensure all rows have the same length
      const maxColumns = Math.max(...jsonData.map(row => row.length));
      console.log(`Max columns in CSV: ${maxColumns}`);

      // Pad all rows to have the same length
      jsonData = jsonData.map(row => {
        while (row.length < maxColumns) {
          row.push('');
        }
        return row;
      });
    } else {
      // Parse the Excel file (ArrayBuffer)
      console.log('Parsing Excel file...');
      const workbook = XLSX.read(fileContent, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];

      // Convert to JSON with headers
      jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
    }

    if (jsonData.length < 3) {
      throw new Error('File does not contain enough data');
    }
    
    // Extract data based on your Excel structure
    const periodLine = jsonData[1]?.[0] || ''; // Period line is typically on row 2 (index 1)
    const headers = jsonData[2];
    const dayHeaders = jsonData[3];
    const dataRows = jsonData.slice(4);

    console.log('Period line:', periodLine);
    console.log('Headers:', headers);
    console.log(`Headers length: ${headers.length}`);
    console.log('Headers columns 30-35:', headers.slice(30, 36));
    console.log('Day headers:', dayHeaders);

    // Extract period from the CSV if available
    let baseMonth = 10; // November (0-indexed: 10 = November)
    let baseYear = new Date().getFullYear();

    if (periodLine && periodLine.includes('Period :')) {
      // Extract the date range part (e.g., "11/29/2025 ~ 12/28/2025")
      const periodMatch = periodLine.match(/Period\s*:\s*(.+)/);
      if (periodMatch) {
        // Extract year and month from period dates
        const dateMatch = periodMatch[1].match(/(\d{1,2})\/(\d{1,2})\/(\d{4})\s*~\s*(\d{1,2})\/(\d{1,2})\/(\d{4})/);
        if (dateMatch) {
          const startMonth = parseInt(dateMatch[1]) - 1; // Convert to 0-indexed
          const startYear = parseInt(dateMatch[3]);
          baseMonth = startMonth;
          baseYear = startYear;
          console.log(`Extracted from Period: Start month=${startMonth + 1}, Year=${startYear}`);
        }
      }
    }

    // Calculate actual period from CSV columns (not from Period line)
    // This ensures we process ALL date columns, not just those in the Period range
    const dateColumns = headers.slice(3).filter(h => !isNaN(parseInt(h))); // Get all numeric headers after column 3
    const firstDay = parseInt(dateColumns[0]);
    const lastDay = parseInt(dateColumns[dateColumns.length - 1]);

    // Determine start and end dates based on the columns
    let startMonth = baseMonth;
    let startYear = baseYear;
    let endMonth = baseMonth;
    let endYear = baseYear;

    // If first day is >= 28, it's likely the end of the previous month
    if (firstDay >= 28) {
      startMonth = baseMonth;
    } else {
      startMonth = baseMonth + 1;
    }

    // If last day < 28, it's likely in the next month, otherwise current month
    if (lastDay >= 28 && lastDay < firstDay) {
      endMonth = baseMonth;
      endYear = baseYear;
    } else if (firstDay >= 28 && lastDay <= 31) {
      endMonth = baseMonth + 1;
      endYear = baseYear;
    } else {
      endMonth = baseMonth + 1;
      endYear = baseYear;
    }

    // Create period string that covers ALL date columns in CSV
    const period = `${startMonth + 1}/${firstDay}/${startYear} ~ ${endMonth + 1}/${lastDay}/${endYear}`;
    console.log(`Calculated Period from CSV columns: ${period}`);

    // Convert to CSV format that matches your existing parser
    let csvContent = 'Attendance Logs\n';
    csvContent += `Period : ${period}\n`;
    csvContent += 'User ID,Name,Department,Date,Time\n';

    const employeeEntries = [];

    console.log(`Using base date: Year ${baseYear}, Month ${baseMonth + 1} (${getMonthName(baseMonth)})`);

    // Extract actual period limits from the Period line for validation
    let periodStartDate = null;
    let periodEndDate = null;
    if (periodLine && periodLine.includes('Period :')) {
      const periodMatch = periodLine.match(/Period\s*:\s*(.+)/);
      if (periodMatch) {
        const dateMatch = periodMatch[1].match(/(\d{1,2})\/(\d{1,2})\/(\d{4})\s*~\s*(\d{1,2})\/(\d{1,2})\/(\d{4})/);
        if (dateMatch) {
          const startMonth = parseInt(dateMatch[1]);
          const startDay = parseInt(dateMatch[2]);
          const startYear = parseInt(dateMatch[3]);
          const endMonth = parseInt(dateMatch[4]);
          const endDay = parseInt(dateMatch[5]);
          const endYear = parseInt(dateMatch[6]);

          periodStartDate = `${startYear}-${startMonth.toString().padStart(2, '0')}-${startDay.toString().padStart(2, '0')}`;
          periodEndDate = `${endYear}-${endMonth.toString().padStart(2, '0')}-${endDay.toString().padStart(2, '0')}`;

          console.log(`Period validation range: ${periodStartDate} to ${periodEndDate}`);
        }
      }
    }

    // Process each employee (variable rows: 2-4 rows per employee)
    // Employees with lunch breaks have 4 rows (check-in, check-out-lunch, check-in-lunch, check-out)
    // We need: FIRST check-in (morning) and LAST check-out (evening)
    for (let i = 0; i < dataRows.length; i++) {
      const currentRow = dataRows[i];

      if (!currentRow || currentRow.length < 4) continue;

      const userId = currentRow[0]?.toString() || '';

      // Skip rows without User ID (these are additional check-in/out rows)
      if (!userId) continue;

      const userName = currentRow[1] || '';
      const department = currentRow[2] || '';

      if (!userName) continue;

      console.log(`Processing employee: ${userName} (ID: ${userId})`);

      // Collect all rows for this employee (including check-in/out rows)
      const employeeRows = [currentRow];
      let j = i + 1;

      // Collect subsequent rows until we find the next employee (row with User ID) or end
      while (j < dataRows.length && (!dataRows[j][0] || !dataRows[j][0].toString().trim())) {
        employeeRows.push(dataRows[j]);
        j++;
      }

      console.log(`  Employee has ${employeeRows.length} rows (check-in/out)`);

      // Debug: Show columns 30-35 data for this employee
      if (userName === 'ADRIANA' || userName === 'AREIFA') {
        console.log(`  DEBUG ${userName} - Row lengths:`, employeeRows.map(r => r.length));
        console.log(`  DEBUG ${userName} - Columns 30-35 data:`);
        for (let c = 30; c <= 35; c++) {
          const colData = employeeRows.map(r => r[c] || 'empty');
          console.log(`    Col ${c} (${headers[c]}): [${colData.join(', ')}]`);
        }
      }

      // Process each date column (starting from column 3)
      console.log(`  Processing columns 3 to ${headers.length - 1} (total ${headers.length - 3} date columns)`);

      // Track the previous day number to detect month transitions
      let previousDayNum = null;
      let currentMonth = baseMonth; // Start with base month (November)

      for (let col = 3; col < headers.length; col++) {
        const dayNumber = headers[col];
        const dayOfWeek = dayHeaders[col];
        const dayNum = parseInt(dayNumber);

        if (isNaN(dayNum)) {
          console.warn(`Invalid day number: ${dayNumber}`);
          continue;
        }

        // FIXED: Detect month transition when day number decreases
        // e.g., going from 30 (Nov) to 1 (Dec), or from 31 (Dec) to 1 (Jan)
        if (previousDayNum !== null && dayNum < previousDayNum) {
          currentMonth++;
          console.log(`  Month transition detected at col ${col}: day ${previousDayNum} -> ${dayNum}, new month = ${currentMonth + 1}`);
        }
        previousDayNum = dayNum;

        // Collect all times for this day from all rows
        const allTimes = employeeRows.map(row => row[col]).filter(t => t);

        // Debug for columns near the end
        if (col >= 30 && (userName === 'ADRIANA' || userName === 'AREIFA')) {
          console.log(`    Col ${col} (day ${dayNumber}): Raw values = [${employeeRows.map(r => r[col] || 'undefined').join(', ')}]`);
          console.log(`    Col ${col} (day ${dayNumber}): After filter = [${allTimes.join(', ')}]`);
        }

        // Skip if no data for this day
        if (allTimes.length === 0) {
          if (col >= 30) console.log(`    Col ${col} (day ${dayNumber}): No data, skipping`);
          continue;
        }

        if (col >= 30) console.log(`    Col ${col} (day ${dayNumber}): Found ${allTimes.length} times - processing...`);

        // FIRST time is check-in (morning), LAST time is check-out (evening)
        const checkInTime = allTimes[0];
        const checkOutTime = allTimes[allTimes.length - 1];

        // FIXED: Use the currentMonth we've been tracking
        const month = currentMonth;
        const year = baseYear;

        const date = `${year}-${(month + 1).toString().padStart(2, '0')}-${dayNum.toString().padStart(2, '0')}`;

        // Validate date is within the period range specified in the CSV
        if (periodStartDate && periodEndDate) {
          if (date < periodStartDate || date > periodEndDate) {
            console.log(`Day ${dayNumber} -> ${date} is outside period range (${periodStartDate} to ${periodEndDate}), skipping`);
            continue;
          }
        }

        console.log(`Day ${dayNumber} (${dayOfWeek}) -> ${date}, Check-in: ${checkInTime}, Check-out: ${checkOutTime}`);

        // Add check-in entry
        if (checkInTime) {
          const time = convertTo24Hour(checkInTime);
          if (time) {
            csvContent += `${userId},${userName},${department},${date},${time}\n`;
            employeeEntries.push({
              employee_id: userId,
              name: userName,
              department: department,
              datetime: `${date} ${time}`,
              status: 'present',
              action: 'IN'
            });
          }
        }
        
        // Add check-out entry
        if (checkOutTime) {
          const time = convertTo24Hour(checkOutTime);
          if (time) {
            csvContent += `${userId},${userName},${department},${date},${time}\n`;
            employeeEntries.push({
              employee_id: userId,
              name: userName,
              department: department,
              datetime: `${date} ${time}`,
              status: 'present',
              action: 'OUT'
            });
          }
        }
      }

      // Skip the rows we've already processed for this employee
      i = j - 1; // -1 because the for loop will increment i
    }

    console.log(`Converted ${employeeEntries.length} entries from XLSX`);
    
    // Calculate actual date range from entries
    let minDate = null;
    let maxDate = null;
    
    if (employeeEntries.length > 0) {
      const dates = employeeEntries.map(entry => entry.datetime.split(' ')[0]);
      minDate = dates.reduce((a, b) => a < b ? a : b);
      maxDate = dates.reduce((a, b) => a > b ? a : b);
    }
    
    return {
      csvContent,
      entries: employeeEntries,
      summary: {
        totalEntries: employeeEntries.length,
        employeeCount: new Set(employeeEntries.map(e => e.employee_id)).size,
        dateRange: {
          start: minDate ? `${minDate} 00:00:00` : null,
          end: maxDate ? `${maxDate} 23:59:59` : null
        }
      }
    };
    
  } catch (error) {
    console.error('Error converting XLSX to CSV:', error);
    throw new Error(`Failed to process Excel file: ${error.message}`);
  }
};

// Helper function to convert time format
const convertTo24Hour = (timeStr) => {
  if (!timeStr) return null;
  
  try {
    let time = timeStr.toString().trim().toUpperCase();
    
    if (!time || time === '-' || time === 'NULL') return null;
    
    if (time.includes('PM') && time.includes(':')) {
      const timePart = time.split(' ')[0];
      const [hours, minutes] = timePart.split(':');
      let hourNum = parseInt(hours);
      
      if (hourNum >= 13 && hourNum <= 23) {
        return `${hours.padStart(2, '0')}:${(minutes || '00').padStart(2, '0')}:00`;
      }
      
      if (hourNum !== 12) {
        hourNum += 12;
      }
      return `${hourNum.toString().padStart(2, '0')}:${(minutes || '00').padStart(2, '0')}:00`;
    }
    
    if (time.includes('AM')) {
      const timePart = time.split(' ')[0];
      const [hours, minutes] = timePart.split(':');
      let hourNum = parseInt(hours);
      
      if (hourNum === 12) {
        hourNum = 0;
      }
      return `${hourNum.toString().padStart(2, '0')}:${(minutes || '00').padStart(2, '0')}:00`;
    }
    
    if (time.includes(':')) {
      const [hours, minutes] = time.split(':');
      const hourNum = parseInt(hours);
      
      if (hourNum < 0 || hourNum > 23) {
        console.warn(`Invalid hour in time: ${timeStr}`);
        return null;
      }
      
      return `${hours.padStart(2, '0')}:${(minutes || '00').padStart(2, '0')}:00`;
    }
    
    console.warn(`Unrecognized time format: ${timeStr}`);
    return null;
  } catch (error) {
    console.warn('Error converting time:', timeStr, error);
    return null;
  }
};

const parseDateTime = (dateTimeStr) => {
  try {
    let parsed = dayjs(dateTimeStr, 'YYYY-MM-DD HH:mm:ss', true);
    if (parsed.isValid()) {
      return parsed.format('YYYY-MM-DD HH:mm:ss');
    }

    parsed = dayjs(dateTimeStr, 'DD-MM-YY HH:mm', true);
    if (parsed.isValid()) {
      const year = parsed.year();
      if (year < 100) {
        parsed = parsed.year(year + (year < 50 ? 2000 : 1900));
      }
      return parsed.format('YYYY-MM-DD HH:mm:ss');
    }

    const formats = [
      'DD-MM-YY H:mm',
      'DD-MM-YY HH:mm:ss',
      'YYYY-MM-DD H:mm',
      'YYYY-MM-DD HH:mm'
    ];

    for (const format of formats) {
      parsed = dayjs(dateTimeStr, format, true);
      if (parsed.isValid()) {
        if (parsed.year() < 100) {
          parsed = parsed.year(parsed.year() + (parsed.year() < 50 ? 2000 : 1900));
        }
        return parsed.format('YYYY-MM-DD HH:mm:ss');
      }
    }

    console.error('Failed to parse date:', dateTimeStr);
    throw new Error(`Invalid date format: ${dateTimeStr}`);
  } catch (error) {
    console.error('Date parsing error:', error, 'for date:', dateTimeStr);
    throw error;
  }
};

const convertStatus = (status) => {
  const statusStr = (status || '').toString().trim();
  
  switch (statusStr) {
    case '00':
    case '0':
    case 'present':
      return 'present';
    case '01':
    case '1':
    case 'late':
      return 'late';
    case '02':
    case '2':
    case 'absent':
      return 'absent';
    default:
      return 'present';
  }
};

export const generateCompleteAttendanceMatrix = async (startDate, endDate, company_id) => {
  try {
    console.log('Generating complete attendance matrix:', { startDate, endDate, company_id });
    
    const { data: employees, error: empError } = await supabase
      .from('employees')
      .select('id, fingerprint_id, first_name, last_name, department')
      .eq('company_id', company_id)
      .eq('status', 'active');
    
    if (empError) throw empError;
    
    if (!employees || employees.length === 0) {
      console.log('No active employees found');
      return [];
    }
    
    const { data: existingRecords, error: recError } = await supabase
      .from('timesheet_daily_records')
      .select('*')
      .eq('company_id', company_id)
      .gte('date', startDate)
      .lte('date', endDate);
    
    if (recError) throw recError;
    
    const { data: settings, error: settingsError } = await supabase
      .from('settings')
      .select('working_hours')
      .eq('company_id', company_id)
      .single();
    
    if (settingsError && settingsError.code !== 'PGRST116') {
      console.warn('Settings fetch error:', settingsError);
    }
    
    const workDays = settings?.working_hours?.work_week || ['1', '2', '3', '4', '5'];
    
    // Generate ALL dates in the range (not just working days)
    const dates = [];
    let currentDate = dayjs(startDate);
    const endDateObj = dayjs(endDate);
    
    while (currentDate.isSameOrBefore(endDateObj, 'day')) {
      dates.push(currentDate.format('YYYY-MM-DD'));
      currentDate = currentDate.add(1, 'day');
    }
    
    console.log(`Generated ${dates.length} total dates for ${employees.length} employees`);
    
    const existingMap = {};
    (existingRecords || []).forEach(record => {
      const key = `${record.employee_id}_${record.date}`;
      existingMap[key] = record;
    });
    
    const completeMatrix = [];
    
    employees.forEach(employee => {
      dates.forEach(date => {
        const key = `${employee.id}_${date}`;
        const dateObj = dayjs(date);
        const isSunday = dateObj.day() === 0; // 0 = Sunday
        
        if (existingMap[key]) {
          const existingRecord = existingMap[key];
          
          // Check if it's Sunday and has no actual times
          const hasActualTimes = existingRecord.check_in && 
                                existingRecord.check_out && 
                                existingRecord.check_in !== '00:00:00' && 
                                existingRecord.check_out !== '00:00:00';
          
          // If Sunday and no actual times, mark as DAY OFF
          if (isSunday && !hasActualTimes) {
            completeMatrix.push({
              ...existingRecord,
              leave_type: 'OFF',
              is_empty: false,
              employees: employee
            });
          } else {
            completeMatrix.push({
              ...existingRecord,
              employees: employee
            });
          }
        } else {
          // For new/empty records
          if (isSunday) {
            // Sundays are automatically DAY OFF
            completeMatrix.push({
              id: `empty_${key}`,
              company_id,
              employee_id: employee.id,
              date,
              check_in: null,
              check_out: null,
              total_hours: 0,
              status: 'day_off',
              is_late: false,
              is_early_out: false,
              is_holiday: false,
              is_sunday: true,
              leave_type: 'OFF',
              notes: null,
              is_empty: false, // Not empty since we're marking it as DAY OFF
              employees: employee
            });
          } else {
            // Regular days remain as empty/absent
            completeMatrix.push({
              id: `empty_${key}`,
              company_id,
              employee_id: employee.id,
              date,
              check_in: null,
              check_out: null,
              total_hours: 0,
              status: 'absent',
              is_late: false,
              is_early_out: false,
              is_holiday: false,
              is_sunday: false,
              leave_type: null,
              notes: null,
              is_empty: true,
              employees: employee
            });
          }
        }
      });
    });
    
    completeMatrix.sort((a, b) => {
      const dateCompare = b.date.localeCompare(a.date);
      if (dateCompare !== 0) return dateCompare;
      
      const nameA = `${a.employees?.first_name || ''} ${a.employees?.last_name || ''}`;
      const nameB = `${b.employees?.first_name || ''} ${b.employees?.last_name || ''}`;
      return nameA.localeCompare(nameB);
    });
    
    console.log(`Complete matrix generated: ${completeMatrix.length} total records`);
    return completeMatrix;
    
  } catch (error) {
    console.error('Error generating complete attendance matrix:', error);
    throw error;
  }
};

const timesheetService = {
  async previewImport(content, fileName = '') {
    try {
      console.log('Starting import preview...', { fileName });

      let parsedData;
      const parser = new FingerprintParser();

      // Check if it's an Excel file OR an Excel-exported CSV
      const isExcelFile = fileName.toLowerCase().endsWith('.xlsx') ||
                         fileName.toLowerCase().endsWith('.xls') ||
                         (typeof content !== 'string' && content instanceof ArrayBuffer);

      // Check if CSV has Excel export format (multi-row headers with Period line)
      const isExcelCSV = !isExcelFile && typeof content === 'string' &&
                        content.includes('Period :') &&
                        content.split('\n').length > 3 &&
                        content.split('\n')[2].includes('User ID,Name,Department');

      if (isExcelFile || isExcelCSV) {
        console.log(isExcelCSV ? 'Processing Excel-exported CSV file...' : 'Processing XLSX file...');
        const conversionResult = convertXLSXToCSVFormat(content, fileName);

        // For Excel format, we don't need the FingerprintParser since we generate entries ourselves
        // Just create the parsedData structure directly
        parsedData = {
          format: 'excel',
          entries: conversionResult.entries,
          summary: conversionResult.summary,
          employees: Array.from(new Set(conversionResult.entries.map(e => ({
            fingerprint_id: e.employee_id,
            name: e.name,
            department: e.department
          }))))
        };

        console.log(`Preview: ${conversionResult.entries.length} entries, date range: ${conversionResult.summary?.dateRange?.start} to ${conversionResult.summary?.dateRange?.end}`);
      } else {
        if (!content || content.trim().length === 0) {
          throw new Error('File is empty');
        }
        
        const format = parser.detectCSVFormat(content);
        console.log('Detected format:', format);
        
        if (format === 'calendar') {
          parsedData = parser.parseCalendarCSV(content);
        } else {
          throw new Error('Unable to parse file. Please ensure it is in the correct format.');
        }
      }
      
      // Generate employee mapping for preview with proper dates
      const employeeMapping = {
        existingEmployees: [],
        newEmployees: [],
        newEmployeeCount: 0
      };
      
      const uniqueEmployees = {};
      parsedData.entries.forEach(entry => {
        if (!uniqueEmployees[entry.employee_id]) {
          uniqueEmployees[entry.employee_id] = {
            fingerprint_id: entry.employee_id,
            name: entry.name || 'Unknown',
            department: entry.department || 'Unassigned'
          };
        }
      });
      
      employeeMapping.mapping = {
        employees: Object.values(uniqueEmployees),
        newEmployees: Object.values(uniqueEmployees)
      };
      
      // Calculate proper first and last entries for each employee
      const employeeDetails = Object.values(uniqueEmployees).map(emp => {
        const empEntries = parsedData.entries.filter(e => e.employee_id === emp.fingerprint_id);
        const sortedEntries = empEntries.sort((a, b) => a.datetime.localeCompare(b.datetime));
        
        return {
          fingerprint_id: emp.fingerprint_id,
          name: emp.name,
          department: emp.department,
          entryCount: empEntries.length,
          firstEntry: sortedEntries.length > 0 ? sortedEntries[0].datetime : null,
          lastEntry: sortedEntries.length > 0 ? sortedEntries[sortedEntries.length - 1].datetime : null
        };
      });
      
      return {
        success: true,
        format: isExcelFile ? 'excel' : 'calendar',
        preview: {
          totalEntries: parsedData.entries.length,
          employeeCount: Object.keys(uniqueEmployees).length,
          dateRange: parsedData.summary?.dateRange,
          employees: employeeDetails,
          employeeMapping: employeeMapping
        },
        rawData: parsedData
      };
    } catch (error) {
      console.error('Preview import failed:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  parseCSV(content, fileName = '') {
    try {
      console.log('Starting file parse', { fileName });

      const isExcelFile = fileName.toLowerCase().endsWith('.xlsx') ||
                         fileName.toLowerCase().endsWith('.xls') ||
                         (typeof content !== 'string' && content instanceof ArrayBuffer);

      // Check if CSV has Excel export format (multi-row headers with Period line)
      const isExcelCSV = !isExcelFile && typeof content === 'string' &&
                        content.includes('Period :') &&
                        content.split('\n').length > 3 &&
                        content.split('\n')[2].includes('User ID,Name,Department');

      if (isExcelFile || isExcelCSV) {
        console.log(isExcelCSV ? 'Processing Excel-exported CSV file in parseCSV...' : 'Processing XLSX file in parseCSV...');
        const conversionResult = convertXLSXToCSVFormat(content, fileName);
        return conversionResult.entries;
      }
      
      const lines = content.trim().split('\n');
      const firstLine = lines[0];
      console.log('First line of file:', firstLine);
      
      if (firstLine.includes('Attendance Logs') || content.includes('Period :')) {
        console.log('Detected calendar-style fingerprint format');
        const parser = new FingerprintParser();
        
        try {
          const parsedData = parser.parseCalendarCSV(content);
          console.log('Parsed calendar data:', parsedData.summary);
          return parsedData.entries;
        } catch (parseError) {
          console.error('Error parsing calendar format:', parseError);
          throw new Error('Failed to parse calendar-style format: ' + parseError.message);
        }
      } else {
        throw new Error('Unsupported file format');
      }
    } catch (error) {
      console.error('Error parsing file:', error);
      throw new Error('Failed to parse file: ' + error.message);
    }
  },

  async ensureEmployeesExist(entries, company_id) {
    try {
      const employeeData = entries.reduce((acc, entry) => {
        if (!acc[entry.employee_id]) {
          acc[entry.employee_id] = {
            fingerprint_id: entry.employee_id,
            name: entry.name
          };
        }
        return acc;
      }, {});

      const employeeIds = Object.keys(employeeData);

      const { data: existingEmployees } = await supabase
        .from('employees')
        .select('fingerprint_id, first_name, last_name')
        .eq('company_id', company_id)
        .in('fingerprint_id', employeeIds);

      const existingIds = new Set(existingEmployees?.map(e => e.fingerprint_id) || []);
      const newEmployeeIds = employeeIds.filter(id => !existingIds.has(id));

      if (newEmployeeIds.length > 0) {
        const newEmployeesList = newEmployeeIds.map(id => ({
          fingerprint_id: id,
          name: employeeData[id].name || 'Unknown'
        }));
        
        throw {
          type: 'NEW_EMPLOYEES_BLOCKED',
          message: `IMPORT BLOCKED: ${newEmployeeIds.length} new employee(s) detected.`,
          newEmployees: newEmployeesList
        };
      }

      const { data: allEmployees } = await supabase
        .from('employees')
        .select('id, fingerprint_id')
        .eq('company_id', company_id)
        .in('fingerprint_id', employeeIds);

      return allEmployees.reduce((acc, emp) => {
        acc[emp.fingerprint_id] = emp.id;
        return acc;
      }, {});
    } catch (error) {
      console.error('Error ensuring employees exist:', error);
      throw error;
    }
  },

  async saveRawEntries(entries, company_id) {
    if (!company_id) throw new Error('Company ID is required');
    
    try {
      console.log("Processing entries:", entries.length);
      const employeeUUIDMap = await this.ensureEmployeesExist(entries, company_id);

      const transformedEntries = entries.map(entry => {
        const transformed = {
          company_id,
          employee_id: employeeUUIDMap[entry.employee_id],
          datetime: entry.datetime,
          status: (entry.status || 'present').substring(0, 10),
          action: (entry.action || '01').substring(0, 2),
          device_id: (entry.device_id || '0001').substring(0, 10),
          created_at: dayjs().toISOString()
        };
        
        if (entries.indexOf(entry) < 3) {
          console.log('Transformed entry:', transformed);
        }
        
        return transformed;
      }).filter(entry => entry.employee_id);

      const existingEntries = await this.getExistingRawEntries(transformedEntries, company_id);
      const newEntries = transformedEntries.filter(entry => 
        !existingEntries.some(existing => 
          existing.employee_id === entry.employee_id &&
          existing.datetime === entry.datetime
        )
      );

      if (newEntries.length === 0) {
        console.log('All entries already exist, skipping import');
        return transformedEntries;
      }

      console.log(`Inserting ${newEntries.length} new entries`);

      const chunkSize = 500;
      for (let i = 0; i < newEntries.length; i += chunkSize) {
        const chunk = newEntries.slice(i, i + chunkSize);
        const { error } = await supabase
          .from('timesheet_raw_entries')
          .insert(chunk);

        if (error) {
          console.error('Insert error:', error);
          throw error;
        }
      }

      console.log(`Inserted ${newEntries.length} new raw entries`);
      return transformedEntries;
    } catch (error) {
      console.error('Error in saveRawEntries:', error);
      throw error;
    }
  },

  async getExistingRawEntries(entries, company_id) {
    try {
      if (entries.length === 0) return [];
      
      const employeeIds = [...new Set(entries.map(entry => entry.employee_id))];
      const dates = entries.map(entry => entry.datetime);
      const startDate = dates.sort()[0].split(' ')[0];
      const endDate = dates.sort().reverse()[0].split(' ')[0];

      const { data, error } = await supabase
        .from('timesheet_raw_entries')
        .select('employee_id, datetime')
        .eq('company_id', company_id)
        .in('employee_id', employeeIds)
        .gte('datetime', startDate)
        .lte('datetime', endDate + ' 23:59:59');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Failed to get existing raw entries:', error);
      return [];
    }
  },

  async processTimesheet(entries, company_id) {
    try {
      const employeeUUIDMap = await this.ensureEmployeesExist(entries, company_id);
      const dailyRecords = [];
      const groupedEntries = {};

      entries.forEach(entry => {
        const date = dayjs(entry.datetime).format('YYYY-MM-DD');
        const key = `${entry.employee_id}_${date}`;
        
        if (!groupedEntries[key]) {
          groupedEntries[key] = [];
        }
        groupedEntries[key].push({
          ...entry,
          parsedDateTime: dayjs(entry.datetime)
        });
      });

      Object.entries(groupedEntries).forEach(([key, dayEntries]) => {
        const [employeeId, date] = key.split('_');
        const uuid = employeeUUIDMap[employeeId];
        
        if (!uuid) {
          console.warn(`No UUID found for employee fingerprint_id: ${employeeId}`);
          return;
        }

        dayEntries.sort((a, b) => a.parsedDateTime.valueOf() - b.parsedDateTime.valueOf());

        const firstEntry = dayEntries[0];
        const lastEntry = dayEntries[dayEntries.length - 1];
        const checkIn = firstEntry.parsedDateTime;
        const checkOut = lastEntry.parsedDateTime;

        const standardStart = dayjs(date).hour(9).minute(0);
        const standardEnd = dayjs(date).hour(17).minute(0);

        const record = {
          company_id,
          employee_id: uuid,
          date,
          check_in: checkIn.format('HH:mm:ss'),
          check_out: checkOut.format('HH:mm:ss'),
          total_hours: Math.max(0, checkOut.diff(checkIn, 'hour', true)).toFixed(2),
          status: 'present',
          is_late: checkIn.isAfter(standardStart),
          is_early_out: checkOut.isBefore(standardEnd),
          is_holiday: false,
          is_sunday: dayjs(date).day() === 0,
          notes: null,
          created_at: dayjs().toISOString(),
          updated_at: dayjs().toISOString()
        };

        dailyRecords.push(record);
      });

      if (dailyRecords.length > 0) {
        const existingRecords = await this.getExistingDailyRecords(dailyRecords, company_id);
        const newRecords = dailyRecords.filter(record => 
          !existingRecords.some(existing => 
            existing.employee_id === record.employee_id &&
            existing.date === record.date
          )
        );

        if (newRecords.length === 0) {
          console.log('All daily records already exist');
          return dailyRecords;
        }

        const chunkSize = 500;
        for (let i = 0; i < newRecords.length; i += chunkSize) {
          const chunk = newRecords.slice(i, i + chunkSize);
          const { error } = await supabase
            .from('timesheet_daily_records')
            .insert(chunk);

          if (error) throw error;
        }

        console.log(`Inserted ${newRecords.length} new daily records`);
      }

      return dailyRecords;
    } catch (error) {
      console.error('Error processing timesheet:', error);
      throw error;
    }
  },

  async getExistingDailyRecords(records, company_id) {
    try {
      if (records.length === 0) return [];
      
      const employeeIds = [...new Set(records.map(record => record.employee_id))];
      const dates = [...new Set(records.map(record => record.date))];

      const { data, error } = await supabase
        .from('timesheet_daily_records')
        .select('employee_id, date')
        .eq('company_id', company_id)
        .in('employee_id', employeeIds)
        .in('date', dates);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Failed to get existing daily records:', error);
      return [];
    }
  },

  generateCompleteAttendanceMatrix
};

export default timesheetService;